/**
 * SSOCallbackHandler
 * Servicio para procesar el callback del SSO desde el backend
 * Principio: Single Responsibility - Solo maneja el parsing y validación del callback
 */

/**
 * Parsea los parámetros del hash de la URL del callback SSO
 * El backend redirige con: #message=...&access=...&refresh=...&user=...
 * @returns {Object|null} Parámetros parseados o null si no hay hash
 */
export function parseCallbackParams() {
  const hash = window.location.hash;
  
  if (!hash || hash.length <= 1) {
    return null;
  }

  try {
    // Remover el # inicial
    const hashContent = hash.substring(1);
    
    // Parsear como URLSearchParams
    const params = new URLSearchParams(hashContent);
    
    const result = {
      message: params.get('message'),
      access: params.get('access'),
      refresh: params.get('refresh'),
      user: params.get('user'),
    };

    // Verificar que tenga los datos mínimos necesarios
    if (!result.access || !result.user) {
      console.warn('[SSO Callback] Hash params incomplete:', result);
      return null;
    }

    return result;
  } catch (error) {
    console.error('[SSO Callback] Error parsing hash params:', error);
    return null;
  }
}

/**
 * Decodifica y parsea el objeto user desde el callback
 * @param {string} userString - String JSON URL-encoded del usuario
 * @returns {Object|null} Objeto usuario parseado o null si falla
 */
export function parseUserData(userString) {
  if (!userString) {
    return null;
  }

  try {
    // El backend envía el user URL-encoded
    const decodedUser = decodeURIComponent(userString);
    
    // Parsear JSON
    const userData = JSON.parse(decodedUser);
    
    console.log('[SSO Callback] User data parsed:', userData);
    return userData;
  } catch (error) {
    console.error('[SSO Callback] Error parsing user data:', error);
    return null;
  }
}

/**
 * Valida que los datos del callback sean válidos
 * @param {Object} callbackData - Datos parseados del callback
 * @returns {{isValid: boolean, message: string, data?: Object}}
 */
export function validateCallbackData(callbackData) {
  if (!callbackData) {
    return {
      isValid: false,
      message: 'No callback data found',
    };
  }

  // Validar tokens
  if (!callbackData.access || typeof callbackData.access !== 'string') {
    return {
      isValid: false,
      message: 'Invalid or missing access token',
    };
  }

  if (!callbackData.refresh || typeof callbackData.refresh !== 'string') {
    return {
      isValid: false,
      message: 'Invalid or missing refresh token',
    };
  }

  // Parsear y validar usuario
  const userData = parseUserData(callbackData.user);
  
  if (!userData) {
    return {
      isValid: false,
      message: 'Invalid user data',
    };
  }

  // Validar campos requeridos del usuario
  const requiredFields = ['id', 'username', 'email'];
  for (const field of requiredFields) {
    if (!userData[field]) {
      return {
        isValid: false,
        message: `Missing required user field: ${field}`,
      };
    }
  }

  return {
    isValid: true,
    message: callbackData.message || 'SSO callback successful',
    data: {
      accessToken: callbackData.access,
      refreshToken: callbackData.refresh,
      user: userData,
    },
  };
}

/**
 * Limpia el hash de la URL después de procesar el callback
 * Esto evita que se reprocese el callback en refreshes
 */
export function clearCallbackHash() {
  if (window.location.hash) {
    // Reemplazar la URL sin el hash (sin recargar la página)
    const urlWithoutHash = window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, urlWithoutHash);
    console.log('[SSO Callback] Hash cleared from URL');
  }
}

/**
 * Procesa el callback completo del SSO
 * @returns {{success: boolean, message: string, accessToken?: string, refreshToken?: string, user?: Object}}
 */
export function processCallback() {
  console.log('[SSO Callback] Processing SSO callback...');

  // 1. Parsear parámetros del hash
  const params = parseCallbackParams();
  
  if (!params) {
    return {
      success: false,
      message: 'No SSO callback data found in URL',
    };
  }

  console.log('[SSO Callback] Callback params found:', {
    hasMessage: !!params.message,
    hasAccess: !!params.access,
    hasRefresh: !!params.refresh,
    hasUser: !!params.user,
  });

  // 2. Validar datos
  const validation = validateCallbackData(params);
  
  if (!validation.isValid) {
    console.error('[SSO Callback] Validation failed:', validation.message);
    return {
      success: false,
      message: validation.message,
    };
  }

  // 3. Limpiar hash de la URL
  clearCallbackHash();

  // 4. Retornar datos procesados
  console.log('[SSO Callback] ✅ Callback processed successfully');
  
  return {
    success: true,
    message: validation.message,
    accessToken: validation.data.accessToken,
    refreshToken: validation.data.refreshToken,
    user: validation.data.user,
  };
}

/**
 * Verifica si la URL actual contiene un callback SSO
 * @returns {boolean}
 */
export function hasCallbackInUrl() {
  const hash = window.location.hash;
  return hash.includes('access=') && hash.includes('user=');
}
